// src/components/ui/forms/InviteTeamMemberForm/InviteTeamMemberFormShadcn.jsx
import React, { useState } from 'react';
import { X, Send, UserPlus } from 'lucide-react';

// shadcn components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../dialog';
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { Textarea } from '../../textarea';
import { RadioGroup, RadioGroupItem } from '../../radio-group';

import { cn } from '../../../../utils/cn';

const InviteTeamMemberFormShadcn = ({
  isOpen,
  onClose,
  onInvite,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    email: '',
    role: 'staff',
    message: ''
  });

  const [errors, setErrors] = useState({});

  const roles = [
    { value: 'admin', label: 'Administrator', description: 'Full access to all features and settings' },
    { value: 'manager', label: 'Manager', description: 'Can manage team, customers, and business operations' },
    { value: 'staff', label: 'Staff', description: 'Basic access to daily operations and customer management' }
  ];

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Call onInvite with the exact parameters TeamProvider expects
    // inviteTeamMember(email, role, message = '')
    await onInvite(formData.email, formData.role, formData.message);

    // Reset form on success
    setFormData({ email: '', role: 'staff', message: '' });
    setErrors({});
  };

  const handleClose = () => {
    setFormData({ email: '', role: 'staff', message: '' });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            Invite Team Member
          </DialogTitle>
          <DialogDescription>
            Send an invitation to join your team with the appropriate role and permissions.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="team.member@company.com"
              className={cn(
                errors.email && "border-red-500 focus-visible:ring-red-500"
              )}
              required
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Role Selection */}
          <div className="space-y-3">
            <Label>Role & Permissions</Label>
            <RadioGroup
              value={formData.role}
              onValueChange={(value) => handleFieldChange('role', value)}
              className="space-y-3"
            >
              {roles.map(role => (
                <div key={role.value} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={role.value}
                    id={role.value}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={role.value}
                    className={cn(
                      "flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200 peer-data-[state=checked]:bg-primary/10 peer-data-[state=checked]:border-primary/50 peer-data-[state=checked]:shadow-lg peer-data-[state=checked]:shadow-primary/25",
                      formData.role === role.value
                        ? "bg-primary/10 border-primary/50 shadow-lg shadow-primary/25"
                        : "bg-muted/30 border-border hover:bg-muted/50"
                    )}
                  >
                    <RadioGroupItem
                      value={role.value}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{role.label}</div>
                      <div className="text-sm text-muted-foreground">{role.description}</div>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Optional Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Personal Message (Optional)</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleFieldChange('message', e.target.value)}
              placeholder="Welcome to the team! Looking forward to working with you."
              rows={3}
              className={cn(
                errors.message && "border-red-500 focus-visible:ring-red-500"
              )}
            />
            {errors.message && (
              <p className="text-sm text-red-500">{errors.message}</p>
            )}
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Sending Invite...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Invitation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteTeamMemberFormShadcn;